import { HttpErrorResponse } from '@angular/common/http';

const MAX_MESSAGE_LENGTH = 500;

/**
 * Erro de validação da API Taskeo (HTTP 400):
 * `{ message, errorKey, errors: string[], validationErrors }`.
 *
 * `errors` traz o motivo real (ex.: pessoa sem acesso ao processo); `message` costuma ser
 * genérica ("Falha na validação."), por isso `errors` tem prioridade.
 */
export function extractApiErrorMessage(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) return null;

  const body = error.error;
  if (typeof body === 'string' && body.trim()) return clipMessage(body.trim());
  if (!body || typeof body !== 'object') return null;

  const record = body as Record<string, unknown>;

  const errors = collectApiValidationErrors(record);
  if (errors.length > 0) return clipMessage(errors.join(' '));

  for (const key of ['message', 'title', 'detail'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return clipMessage(value.trim());
  }

  return null;
}

/**
 * Lista de mensagens de validação (`errors: string[]` da API Taskeo; também aceita o formato
 * ASP.NET `errors: { campo: string[] }`). Vazia quando o erro não é de validação.
 */
export function collectApiValidationErrors(source: unknown): string[] {
  const record =
    source instanceof HttpErrorResponse
      ? (source.error as unknown)
      : source;
  if (!record || typeof record !== 'object') return [];

  const errors = (record as Record<string, unknown>)['errors'];

  if (Array.isArray(errors)) {
    return errors
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }

  if (errors && typeof errors === 'object') {
    const flattened: string[] = [];
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (!Array.isArray(value)) continue;
      for (const entry of value) {
        if (typeof entry === 'string' && entry.trim()) flattened.push(entry.trim());
      }
    }
    return flattened;
  }

  return [];
}

function clipMessage(text: string): string {
  return text.length > MAX_MESSAGE_LENGTH ? `${text.slice(0, MAX_MESSAGE_LENGTH)}…` : text;
}
